/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapValues, uniq, values } from "lodash-es";
import { SupabaseService } from "../../../server/supabase/base-supabase-service";
import type { UUID } from "../../../utils/uuid";
import {
  type ReplaceInfo,
  extractEmailLinks,
  extractSimpleLink,
  extractSlackLinks,
  extractTeamsLinks,
  extractTwilioLinks,
  replaceLinksWithHyperlinks,
  thisIsALinkJustDoIt,
} from "./hyperlink-utils";

export class SupabaseHyperlinkService extends SupabaseService<"hyperlink"> {
  table = "hyperlink" as const;

  async doUpsert(info: ReplaceInfo, moment_id: UUID) {
    return this.log_errors(
      await this.client.rpc("upsert_hyperlinks", {
        links: uniq(values(info.replaces)).map((link) => ({ url: link, moment_id })),
      }),
    );
  }
}

/** Extract URLs from the body and upsert them into the hyperlink table without
 *  rewriting the body. Used on draft save so follow-ups can target specific
 *  links before the parent moment is published. Publish-time extraction still
 *  runs via extractAndStoreLinks to do the body rewriting for click tracking. */
export async function upsertHyperlinks(
  body: string,
  channel: "work_email" | "personal_email" | "slack" | "teams" | "twilio",
  moment_id: UUID,
  hyperlinkService: SupabaseHyperlinkService,
): Promise<void> {
  const info = extractByChannel(body, channel);
  if (uniq(values(info.replaces)).length === 0) return;
  await hyperlinkService.doUpsert(info, moment_id);
}

function extractByChannel(
  body: string,
  channel: "work_email" | "personal_email" | "slack" | "teams" | "twilio",
): ReplaceInfo {
  switch (channel) {
    case "work_email":
    case "personal_email":
      return extractEmailLinks(body);
    case "slack":
      return extractSlackLinks(body);
    case "teams":
      return extractTeamsLinks(body);
    case "twilio":
      return extractTwilioLinks(body);
  }
}

export async function extractAndStoreLinks(
  text: string,
  moment_id: UUID,
  mode: "teams" | "slack" | "email" | "twilio" | "simple" | "slack_rich_text" | "link",
  hyperlinkService: SupabaseHyperlinkService,
): Promise<string> {
  const info = {
    teams: extractTeamsLinks,
    slack: (txt: string) => extractSlackLinks(txt, "mrkdwn"),
    slack_rich_text: (txt: string) => extractSlackLinks(txt, "rich_text"),
    email: extractEmailLinks,
    twilio: extractTwilioLinks,
    simple: extractSimpleLink,
    link: thisIsALinkJustDoIt,
  }[mode](text);

  const { data, error } = await hyperlinkService.doUpsert(info, moment_id);

  if (!data || error) {
    console.error("Failed to upsert hyperlinks:", error);
    throw new Error("Failed to upsert_hyperlinks");
  }

  const dataByUrl: { [url: string]: UUID } = data.reduce(
    (prev, current) => ({ ...prev, [current.url]: current.id }),
    {},
  );

  return replaceLinksWithHyperlinks({
    body: info.body,
    replaces: mapValues(info.replaces, (x) => ({ original: x, id: dataByUrl[x]! })),
  });
}
